// Settings Types

export interface Location {
  id: string;
  name: string;
  code: string; // 3-4 character unique location code
  notes?: string;
  address?: string;
  mapLink?: string;
  locationManager?: string;
  assetCount: number;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  subLocations?: SubLocation[];
}

export interface SubLocation {
  id: string;
  name: string;
  notes?: string;
  locationManager?: string;
  assetCount: number;
  parentLocationId: string;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface Category {
  id: string;
  code: string;
  description?: string;
  defaultMethod: DepreciationMethod;
  defaultPercentage: number;
  defaultPeriodYears: number;
  defaultFrequency: DepreciationFrequency;
  userField1?: string;
  userField2?: string;
  userField3?: string;
  doNotDepreciate: boolean;
  assetCount: number;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  subCategories?: SubCategory[];
}

export interface SubCategory {
  id: string;
  name: string;
  notes?: string;
  assetCount: number;
  parentCategoryId: string;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export type DepreciationMethod = 'Straight Line' | 'Declining Balance' | 'Sum of Years Digits' | 'Units of Production';
export type DepreciationFrequency = 'Monthly' | 'Quarterly' | 'Annually';

export interface CategoryDefaults {
  method: string;
  percentage: number;
  years: number;
  frequency: string;
  nominalAccounts?: {
    assetCost?: { code: string; name: string };
    depreciationBS?: { code: string; name: string };
    depreciationPL?: { code: string; name: string };
    disposals?: { code: string; name: string };
  };
}

export interface Equipment {
  id: string;
  code: string;
  description?: string;
  acquisitionValue: number;
  acquiredDate?: string;
  linkedAssetId?: string;
  locationId?: string;
  
  // User Fields
  userText1?: string;
  userText2?: string;
  userText3?: string;
  userText4?: string;
  userText5?: string;
  userText6?: string;
  userValue1?: number;
  userValue2?: number;
  userValue3?: number;
  userDate1?: string;
  userDate2?: string;
  userDate3?: string;
  userOption1?: string;
  userOption2?: string;
  userOption3?: string;
  
  notes?: string;
  
  // Supplier Information
  supplier?: EquipmentSupplier;
  
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface EquipmentSupplier {
  code?: string;
  name?: string;
  telephone?: string;
  email?: string;
  website?: string;
  hirePurchase?: boolean;
  invoiceReference?: string;
  purchaseOrderReference?: string;
  warrantyStartDate?: string;
  warrantyFinishDate?: string;
  warrantyCost?: number;
  addresses?: SupplierAddress[];
}

export interface SupplierAddress {
  id: string;
  type: 'Billing' | 'Shipping' | 'Other';
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
}

export interface DisposalReason {
  id: string;
  code: string;
  description: string;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface NominalAccount {
  code: string;
  name: string;
}

export interface CostCentre {
  code: string;
  name: string;
}

// Period pattern presets for Period Ratio depreciation
export type PeriodPatternPreset = '4-4-5' | '4-5-4' | '5-4-4' | 'equal' | 'custom';

export interface PeriodPatternConfig {
  preset: PeriodPatternPreset;
  periodRatios: number[];
}

// Predefined period patterns (weeks per period for 12 periods)
export const PERIOD_PATTERN_PRESETS: Record<Exclude<PeriodPatternPreset, 'custom'>, { label: string; description: string; ratios: number[] }> = {
  '4-4-5': {
    label: '4-4-5 (Standard UK)',
    description: 'Periods 3, 6, 9, 12 have 5 weeks; others have 4 weeks',
    ratios: [4, 4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5],
  },
  '4-5-4': {
    label: '4-5-4',
    description: 'Periods 2, 5, 8, 11 have 5 weeks; others have 4 weeks',
    ratios: [4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5, 4],
  },
  '5-4-4': {
    label: '5-4-4',
    description: 'Periods 1, 4, 7, 10 have 5 weeks; others have 4 weeks',
    ratios: [5, 4, 4, 5, 4, 4, 5, 4, 4, 5, 4, 4],
  },
  'equal': {
    label: 'Equal Weeks',
    description: 'Each period has approximately 4.33 weeks (52/12)',
    ratios: [4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33, 4.33],
  },
};

// Helper to get total weeks from period ratios
export function getTotalWeeks(ratios: number[]): number {
  return ratios.reduce((sum, val) => sum + val, 0);
}

// Helper to validate period ratios (should sum to approximately 52)
export function validatePeriodRatios(ratios: number[]): { valid: boolean; message?: string } {
  const total = getTotalWeeks(ratios);
  if (ratios.length !== 12) {
    return { valid: false, message: 'Must have exactly 12 periods' };
  }
  if (ratios.some(r => r <= 0)) {
    return { valid: false, message: 'All periods must have positive weeks' };
  }
  if (total < 51 || total > 53) {
    return { valid: false, message: `Total weeks (${total.toFixed(1)}) should be approximately 52` };
  }
  return { valid: true };
}

// Period Management Types
export interface PeriodSettings {
  id: string;
  currentYear: number;
  currentPeriod: number;
  periodStatus: string;
  autoSyncEnabled: boolean;
  lastSyncedAt?: string;
  lastSyncedBy?: string;
  lastSyncSource?: string;
  lastPeriodEndAt?: string;
  lastPeriodEndBy?: string;
  createdBy?: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  displayPeriod: string;
}

export interface UpdatePeriodSettings {
  currentYear: number;
  currentPeriod: number;
  autoSyncEnabled: boolean;
  modifiedBy?: string;
}

export interface RunPeriodEndRequest {
  runDepreciation: boolean;
  triggeredBy?: string;
}

export interface RunPeriodEndResponse {
  success: boolean;
  errorMessage?: string;
  previousYear: number;
  previousPeriod: number;
  newYear: number;
  newPeriod: number;
  periodEndTimestamp?: string;
  depreciationResult?: DepreciationRunResult;
}

export interface DepreciationRunResult {
  totalAssetsProcessed: number;
  successfulTransactions: number;
  failedTransactions: number;
  totalDepreciationAmount: number;
  transactions: DepreciationTransactionSummary[];
  errors: string[];
}

export interface DepreciationTransactionSummary {
  transactionId: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  depreciationAmount: number;
  success: boolean;
  errorMessage?: string;
}

export interface SyncPeriodRequest {
  syncedBy?: string;
}

export interface SyncPeriodResponse {
  success: boolean;
  errorMessage?: string;
  syncedYear?: number;
  syncedPeriod?: number;
  syncTimestamp?: string;
  syncSource?: string;
}

export interface AssetOptions {
  general: {
    assetAutoNumbering: boolean;
    doNotAllowNegativeBoughtValue: boolean;
    defaultBaseValueOn: 'remaining' | 'original';
    servicePeriodDefaults: number;
  };

  promptNominalUpdate: {
    depreciation: boolean;
    addition: boolean;
    revaluation: boolean;
    disposal: boolean;
    transfer: boolean;
  };

  updateNominal: {
    includeResourceOnNominalJournals: boolean;
    summariseJournals: boolean;
  };

  transactions: {
    allowZeroValueAdditions: boolean;
    forceAllDepreciationBeforePeriodEnd: boolean;
    doNotReverseInPeriodDepreciation: boolean;
    
    // Nominal Accounts
    nominalAccounts: {
      assetCost?: string;
      depreciationBS?: string;
      depreciationPL?: string;
      disposal?: string;
      revaluationBS?: string;
      revaluationPL?: string;
      disposalsBankAccount?: string;
      additionsWIPBankAcc?: string;
    };
    
    // Costing
    costing: {
      depreciation?: string;
      addition?: string;
      disposals?: string;
      revaluation?: string;
    };
    
    // Post Costing Transactions
    postCostingTransactions: {
      depreciation: boolean;
      additions: boolean;
      revaluations: boolean;
      disposals: boolean;
    };
    
    // Post CREDIT to Costing
    postCreditToCosting: {
      depreciation: boolean;
      additions: boolean;
      revaluations: boolean;
      disposals: boolean;
    };
  };

  defaultSettings: {
    defaultMethod: 'Straight Line' | 'Reducing Balance' | 'Sum of Digits' | 'Period Ratio' | 'None';
    defaultPercentage: number;
    defaultPeriodYears: number;
    defaultFrequency: 'Monthly' | 'Quarterly' | 'Half Yearly' | 'Yearly' | 'N/A';
    doNotDepreciate: boolean;
    forceTransfersOnChangingCategory: boolean;
  };
  
  periods: {
    preset: PeriodPatternPreset;
    periodRatios: number[];
  };
}
